import { API, getRequest } from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';

import type { Invoice } from './invoices.dto';

/**
 * A task's invoices are a handful at most — this is not a list anyone pages through,
 * it is the couple of rows a task card prints under "Invoices".
 */
const TASK_INVOICES_LIMIT = 50;

/**
 * The invoices raised against one task, and nothing else.
 *
 * Deliberately not `useInvoice().getInvoices({ taskId })`: that writes the shared
 * `invoices` slice which the expenses lists, the budget page and the invoices widget all
 * render from, so opening a task card replaced whatever those were showing with this
 * task's two rows. It also asked for `limit=1000` on the way. This returns the items and
 * touches no store, so a card can hold them in its own state.
 */
export const getInvoicesByTask = async (taskId: string): Promise<Invoice[]> => {
  const params = generateSearchParams({ limit: TASK_INVOICES_LIMIT, page: 1, task_id: taskId });
  const response = await getRequest<{ items: Invoice[] }>(API.EXPENSES, `/invoice?${params}`);
  return response.data.items ?? [];
};

/** Enough that the service can never page the set — see `getAllInvoices`. */
const ALL_INVOICES_LIMIT = 99999;

/**
 * Every invoice on the vessel in one answer.
 *
 * The service filters by a single `task_id`, so a list that prints one column off each
 * row's invoice — a provider, a cost, a paperclip count — has no way to ask for "the
 * invoices of these twelve tasks". A request per row is the alternative, and it grows
 * with the page; this is one request, cached, and shared by every surface that resolves
 * a task's invoice.
 */
export const getAllInvoices = async (): Promise<Invoice[]> => {
  const params = generateSearchParams({ limit: ALL_INVOICES_LIMIT, page: 1 });
  const response = await getRequest<{ items: Invoice[] }>(API.EXPENSES, `/invoice?${params}`);
  return response.data.items ?? [];
};
